import React, { forwardRef, memo, useEffect, useRef, useState } from "react";
import { reduce, isEqual } from "lodash";
import { ui, colors } from "../constants";
import { SuggestionsView } from "./SuggestionsView";
import styled from "@emotion/styled";
import { Clear, Done, NavigateNext } from "@mui/icons-material";
import { CommonIconButton, usePrevious } from "./Various";
import { useSettings } from "./SettingsProvider";

const AA = styled.div`
  position: absolute;
  box-shadow: none;
  top: 0px;
  box-shadow: ${(props) =>
    props.searchFieldFocus ? `0px 2px 7px 0px ${colors.grey50}` : "none"};
  outline: 1px solid ${colors.grey30};
  width: 100%;
  z-index: 10;
`;

const Main = styled.div`
  height: 37px;
  position: relative;
  display: flex;
  width: 100%;
`;

const StyledSuggestionsView = styled(SuggestionsView)`
  width: 100%;
  box-shadow: ${ui.boxShadow};
  max-height: ${(props) => props.maxHeight}px;
`;

const SearchInput = styled.input`
  flex-grow: 1;
  border: none;
  font-size: 1.1em;
  padding: none;
  margin-left: 10px;
  background-color: white;
`;

const SearchField = styled.div`
  display: flex;
  background-color: white;
  align-items: stretch;
  height: 37px;
  transition: border-color 0.2s;
  overflow: hidden;
  border: none;
  outline: ${(props) =>
    props.searchFieldFocus ? "none" : `1px solid ${colors.grey30}`};

  /* border: ${(props) =>
    props.focus ? `1px solid ${colors.grey80}` : `1px solid ${colors.grey80}`};
  outline: ${(props) =>
    props.focus ? `1px solid ${colors.grey80}` : "none"}; */
`;

export const SearchView = forwardRef(
    (
      {
        filterView,
        suggestions,
        categoriesExpanded,
        searchTerms,
        searchCategory,
        resultCount,
        onExpandCategory,
        onReduceCategory,
        searchMode,
        onSelectCategoryItem,
        onSelectAll,
        onSelectCategory,
        onChangeSearchValue,
        onRemoveSearchCategory,
        searchValue,
        onLoad,
        searchHitCount,
        expansion,
        maxHeight,
        suggestedWords,
        selectedItems,
        addSelection,
        removeSelection,
        setSelectedItems,
        ...other
      },
      ref,
    ) => {
      const { settings } = useSettings();
      const lang = settings.lang;
      const searchFieldRef = useRef(null);
      const totalRef = useRef(null);
      const searchInputRef = useRef(null);

      const [searchFieldFocus, setSearchFieldFocus] = useState(false);
      const [focusIndex, setFocusIndex] = useState({ category: -1, item: -1 });
      
      const prevSuggestions = usePrevious(suggestions);
      const prevCategoriesExpanded = usePrevious(categoriesExpanded);
      const prevSuggestedWords = usePrevious(suggestedWords);

      useEffect(() => {
        if(!isEqual(prevSuggestions, suggestions) || !isEqual(prevSuggestedWords, suggestedWords)) {
          setFocusIndex({category: -1, item: -1});
        }
        else if(focusIndex.category !== -1 && !isEqual(prevCategoriesExpanded, categoriesExpanded)) {
          let cat = Object.keys(suggestions)[focusIndex.category]
          if(prevCategoriesExpanded.includes(cat) && !categoriesExpanded.includes(cat)) {
            setFocusIndex({category: focusIndex.category, item: -1});
          }
        }
      })

      useEffect(() => {
        if (ref) {
          if (typeof ref === "function") {
            // handle callback refs
            ref(searchFieldRef.current);
          } else {
            // handle object refs
            ref.current = searchFieldRef.current;
          }
        }
      }, []);

      useEffect(() => {
        if (searchInputRef.current) {
          // searchInputRef.current.focus();
          // setTimeout(() => searchInputRef.current.focus(), 100)
        }
      }, [searchMode]);

      const keyDown = (event) => {
        
        const cats = Object.keys(suggestions);
      
        if (event.key === 'ArrowUp') {
          if(focusIndex.category == -1) {
            if(focusIndex.item == 0) {
              setFocusIndex({ category: -1, item: -1 });
            } else if(focusIndex.item > 0) {
              const newItem = focusIndex.item-1;
              setFocusIndex({ category: -1, item: newItem });
              onChangeSearchValue(suggestedWords[newItem].word, false);
            }
          }
          else if (focusIndex.category == 0) {
            if (focusIndex.item == -1) {  
              if(suggestedWords.length > 0) {
                const newItem = suggestedWords.length-1;
                setFocusIndex({ category: -1, item: newItem });
                onChangeSearchValue(suggestedWords[newItem].word, false);
              } else {
                setFocusIndex({ category: -1, item: -1 });
              }
            } else {
              setFocusIndex({ category: focusIndex.category, item: focusIndex.item-1 });
            }
          } else {
            if (focusIndex.item == -1) {
              let catIndex = focusIndex.category-1;
              setFocusIndex({ category: catIndex, item: suggestions[cats[catIndex]].results.length-1 });
            } else {
              setFocusIndex({ category: focusIndex.category, item: focusIndex.item-1 });
            }
          }
        }
        else if (event.key === 'ArrowDown') {
          if(focusIndex.category == -1) {
            if (focusIndex.item == -1) {
              if(suggestedWords.length > 0) {
                setFocusIndex({ category: -1, item: 0 });
                onChangeSearchValue(suggestedWords[0].word, false);
              } else if(cats.length > 0) {
                setFocusIndex({ category: 0, item: -1 });
              }
            }
            else if (focusIndex.item < suggestedWords.length-1) {
              const newItem = focusIndex.item+1;
              setFocusIndex({ category: -1, item: newItem });
              onChangeSearchValue(suggestedWords[newItem].word, false);
            }
            else if (cats.length > 0) {
              setFocusIndex({ category: 0, item: -1 });
            }
          }
          else if (focusIndex.category === cats.length - 1) {
            if (focusIndex.item < suggestions[cats[focusIndex.category]].results.length - 1) {  
              setFocusIndex({ category: focusIndex.category, item: focusIndex.item+1 });
            }
          } else {
            if (focusIndex.item < suggestions[cats[focusIndex.category]].results.length - 1) {  
              setFocusIndex({ category: focusIndex.category, item: focusIndex.item+1 });
            } else {
              setFocusIndex({ category: focusIndex.category+1, item: -1 });
            }
          }
        }
        else if(event.key === 'Enter') {
          if(focusIndex.category == -1) {
            if (focusIndex.item == -1) {
              onSelectAll()
            } else {
              onSelectAll(suggestedWords[focusIndex.item].word);
            }
          } 
          else {
            let category = cats[focusIndex.category]
            if(focusIndex.item == -1) {
              let expanded = categoriesExpanded.includes(category)
              if(expanded) {
                onReduceCategory(category)
              } else {
                onExpandCategory(category)
              }
            } else {
              onSelectCategoryItem([suggestions[category].results[focusIndex.item]]);
            }
          }
        }
      }

      return (
        <Main
          onKeyDown={keyDown}
          ref={totalRef}
          {...other}>
          <AA searchFieldFocus={searchFieldFocus}>
            <SearchField
              searchFieldFocus={searchFieldFocus}
              ref={searchFieldRef}
            >
              <SearchInput
                type="text"
                value={searchValue}
                ref={searchInputRef}
                placeholder={ui.labels.searchPlaceholder[lang]}
                onBlur={() => setSearchFieldFocus(false)}
                onFocus={() => setSearchFieldFocus(true)}
                onChange={(event) => onChangeSearchValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "ArrowUp") event.preventDefault();
                }}
              />
              {/* {searchMode === "key" &&
                Object.keys(suggestions).length === 0 &&
                resultCount > 1 && (
                  <CommonIconButton
                    tooltip={ui.tooltips.typeDot[lang]}
                    log="searchfield_type_dot"
                    icon={<NavigateNext />}
                    onMouseDown={(event) => {
                      onChangeSearchValue(searchValue + ".");
                      event.preventDefault();
                    }}
                  />
                )} */}
              {/* {searchMode === "meta" && searchValue.length > 2 && (
                <CommonIconButton
                  tooltip={ui.tooltips.applySearch[lang]}
                  log="searchfield_apply"
                  icon={<Done />}
                  onMouseDown={(event) => {
                    onSelectAll(searchValue);
                    event.preventDefault();
                  }}
                />
              )} */}
              <CommonIconButton
                tooltip={ui.tooltips.clearSearch[lang]}
                log="searchfield_clear"
                icon={<Clear />}
                onMouseDown={(event) => {
                  onChangeSearchValue("");
                  event.preventDefault();
                }}
              />
            </SearchField>
            {searchFieldFocus && (
              <StyledSuggestionsView
                maxHeight={maxHeight - 37}
                suggestions={suggestions}
                categoriesExpanded={categoriesExpanded}
                searchTerms={searchTerms}
                searchValue={searchValue}
                onExpandCategory={onExpandCategory}
                onReduceCategory={onReduceCategory}
                searchCategory={searchCategory}
                onRemoveSearchCategory={onRemoveSearchCategory}
                onSelectAll={onSelectAll}
                resultCount={resultCount}
                searchMode={searchMode}
                focusIndex={focusIndex}
                setFocusIndex={setFocusIndex}
                onSelectCategoryItem={onSelectCategoryItem}
                onSelectCategory={onSelectCategory}
                onLoad={onLoad}
                searchHitCount={searchHitCount}
                suggestedWords={suggestedWords}
                onChangeSearchValue={onChangeSearchValue}
                expansion={expansion}
                selectedItems={selectedItems}
                addSelection={addSelection}
                removeSelection={removeSelection}
                setSelectedItems={setSelectedItems}
              />
            )}
          </AA>
        </Main>
      );
    },
  );
